import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import { Tab } from '@headlessui/react';
import { 
  FaCogs, FaRegLightbulb, FaChartPie, FaChartLine
} from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// Import the separated components
import CategorySpecific from '../components/IntelliSpend/CategorySpecific';
import SmartInsight from '../components/IntelliSpend/SmartInsight';
import ControlPanel from '../components/IntelliSpend/ControlPanel';

const IntelliSpend = () => {
  const { currentUser } = useAuth();
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [currencies, setCurrencies] = useState(['GBP']);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user settings and categories
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        // Fetch settings for currencies
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const userSettings = settingsDoc.data();
          if (userSettings.currencies && userSettings.currencies.length > 0) {
            setCurrencies(userSettings.currencies);
            setSelectedCurrency(userSettings.currencies[0]);
          }
        }
        
        // Fetch categories
        const categoriesDoc = await getDoc(doc(db, 'categories', currentUser.uid));
        if (categoriesDoc.exists() && categoriesDoc.data().items) {
          setCategories(categoriesDoc.data().items);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto py-5 px-4 max-w-6xl">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            IntelliSpend
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Get deep insights into your spending patterns and financial health
          </p>
        </motion.div>

        {/* Collapsible Control Panel */}
        <ControlPanel 
          showControlPanel={showControlPanel}
          setShowControlPanel={setShowControlPanel}
          currencies={currencies}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
        />

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md"
        >
          <Tab.Group>
            <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
              <Tab.List className="flex max-w-3xl mx-auto">
                <Tab className={({ selected }) => `
                  flex-1 px-4 py-3 text-sm font-medium focus:outline-none whitespace-nowrap border-b-2 transition-colors
                  ${selected 
                    ? 'text-indigo-600 border-indigo-600 dark:text-indigo-400 dark:border-indigo-400' 
                    : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}
                `}>
                  <div className="flex items-center justify-center space-x-1.5">
                    <FaChartPie className="w-4 h-4" />
                    <span>Categories</span>
                  </div>
                </Tab>
                
                <Tab className={({ selected }) => `
                  flex-1 px-4 py-3 text-sm font-medium focus:outline-none whitespace-nowrap border-b-2 transition-colors
                  ${selected 
                    ? 'text-indigo-600 border-indigo-600 dark:text-indigo-400 dark:border-indigo-400' 
                    : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}
                `}>
                  <div className="flex items-center justify-center space-x-1.5">
                    <FaRegLightbulb className="w-4 h-4" />
                    <span>Insights</span>
                  </div>
                </Tab>
              </Tab.List>
            </div>
            
            <Tab.Panels className="p-4 md:p-6">
              {isLoading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  {/* Category Analysis Panel */}
                  <Tab.Panel>
                    {categories.length > 0 ? (
                      <CategorySpecific 
                        categories={categories} 
                        selectedCategories={selectedCategories} 
                        setSelectedCategories={setSelectedCategories}
                        selectedCurrency={selectedCurrency}
                      />
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400">You don't have any categories defined yet.</p>
                        <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">Add categories in your Settings to analyse your expenses by category.</p>
                      </div>
                    )}
                  </Tab.Panel>
                  
                  {/* Smart Insights Panel */}
                  <Tab.Panel>
                    <SmartInsight 
                      selectedCurrency={selectedCurrency}
                    />
                  </Tab.Panel>
                </>
              )}
            </Tab.Panels>
          </Tab.Group>
        </motion.div>
        
        {/* Footer hint */}
        <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Adjust your settings to see customised financial calculations
        </div>
      </div>
    </div>
  );
};

export default IntelliSpend;
